import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Heart, MessageCircle, Share2, Trash2, EyeOff, User } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'

const PostCard = ({ post, onDelete }) => {
  const { user, api } = useAuth()
  const [isLiked, setIsLiked] = useState(post.isLiked || false)
  const [likeCount, setLikeCount] = useState(post.likeCount || 0)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleLike = async () => {
    if (!user) {
      toast.error('Please sign in to like posts')
      return
    }

    try {
      const response = await api.post(`/posts/${post._id}/like`)
      if (response.data.success) {
        setIsLiked(response.data.data.liked)
        setLikeCount(response.data.data.likeCount)
      }
    } catch (error) {
      toast.error('Failed to like post')
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this confession?')) return
    
    setIsDeleting(true)
    try {
      const response = await api.delete(`/posts/${post._id}`)
      if (response.data.success) {
        toast.success('Confession deleted')
        onDelete?.(post._id)
      }
    } catch (error) {
      toast.error('Failed to delete confession')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/post/${post._id}`)
      toast.success('Link copied to clipboard')
    } catch {
      toast.error('Failed to copy link')
    }
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {post.isAnonymous ? (
              <div className="w-10 h-10 bg-anonymous-100 rounded-full flex items-center justify-center">
                <EyeOff className="w-5 h-5 text-anonymous-600" />
              </div>
            ) : (
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-primary-600" />
              </div>
            )}
            <div>
              <p className="font-medium text-gray-900">
                {post.isAnonymous ? 'Anonymous' : post.authorDisplay}
              </p>
              <p className="text-xs text-gray-500">
                {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
              </p>
            </div>
          </div>
          
          {post.isAuthor && (
            <div className="flex items-center space-x-1">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-gray-800 whitespace-pre-wrap">{post.content}</p>
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLike}
              className={`flex items-center space-x-1 text-sm transition-colors ${
                isLiked ? 'text-red-600' : 'text-gray-500 hover:text-red-600'
              }`}
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
              <span>{likeCount}</span>
            </button>
            
            <button
              className="flex items-center space-x-1 text-sm text-gray-500 hover:text-primary-600 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              <span>{post.commentCount || 0}</span>
            </button>
          </div>

          <button
            onClick={handleShare}
            className="flex items-center space-x-1 text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default PostCard